import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { doc, getDoc, collection, query, where, getDocs, orderBy, addDoc, updateDoc, increment, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { PostCard } from '../components/Feed/PostCard';
import { useFeedData } from '../hooks/useFeedData';

const formatRelativeTime = (timestamp) => {
  if (!timestamp) return 'Just now';
  let date;
  if (timestamp.toDate) date = timestamp.toDate();
  else if (timestamp.seconds) date = new Date(timestamp.seconds * 1000);
  else date = new Date(timestamp);
  
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
};

export const ViewPostPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const currentUser = auth.currentUser;
  
  const [post, setPost] = useState(null);
  const [authorProfile, setAuthorProfile] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentProfiles, setCommentProfiles] = useState({});
  const [commentText, setCommentText] = useState('');
  const [isCommenting, setIsCommenting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isRestricted, setIsRestricted] = useState(false);

  const {
    likedPosts,
    savedPosts,
    handleLikeUpdate,
    handleSaveUpdate
  } = useFeedData();

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        const postRef = doc(db, 'sparkposts', id);
        const postSnap = await getDoc(postRef);

        if (!postSnap.exists()) {
          setError(true);
          return;
        }

        const postData = { id: postSnap.id, ...postSnap.data() };
        
        // Fetch Author
        const authorSnap = await getDoc(doc(db, 'users', postData.uid));
        if (authorSnap.exists()) {
          setAuthorProfile(authorSnap.data());
        } else {
          setAuthorProfile({ name: 'Anonymous' });
        }

        // Visibility Check
        let allowed = true;
        if (currentUser && postData.uid !== currentUser.uid) {
          const viz = postData.visibility || 'global';
          if (viz === 'campus') {
             const mySnap = await getDoc(doc(db, 'users', currentUser.uid));
             const myCollege = mySnap.exists() ? mySnap.data().college : '';
             if (!myCollege || myCollege !== postData.college) allowed = false;
          } else if (viz === 'followers') {
             const followSnap = await getDoc(doc(db, 'users', currentUser.uid, 'following', postData.uid));
             if (!followSnap.exists() || !followSnap.data().isActive) allowed = false;
          }
        }

        if (!allowed) {
          setIsRestricted(true);
          setPost(postData);
        } else {
          setPost(postData);
          
          // Fetch Comments
          const q = query(collection(db, 'postcomments'), where('postId', '==', id), orderBy('createdAt', 'desc'));
          const commentsSnap = await getDocs(q);
          const fetchedComments = commentsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          setComments(fetchedComments);
          
          // Fetch profiles for comments
          const uids = new Set(fetchedComments.map(c => c.userId));
          const pMap = {};
          await Promise.all([...uids].map(async (uid) => {
            const uSnap = await getDoc(doc(db, 'users', uid));
            if (uSnap.exists()) pMap[uid] = uSnap.data();
            else pMap[uid] = { name: 'Anonymous' };
          }));
          setCommentProfiles(pMap);
        }

      } catch (err) {
        console.error("Failed to fetch post", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [id, currentUser]);

  const handlePostComment = async () => {
    if (!commentText.trim() || isCommenting || !currentUser) return;
    setIsCommenting(true);
    try {
      const newComment = {
        postId: post.id,
        userId: currentUser.uid,
        content: commentText.trim(),
        createdAt: new Date(), // using local date for optimistic UI
        likes: 0,
        replies: []
      };

      // 1. Add comment doc
      const docRef = await addDoc(collection(db, 'postcomments'), {
         ...newComment,
         createdAt: serverTimestamp()
      });
      
      // 2. Increment post commentCount
      await updateDoc(doc(db, 'sparkposts', post.id), {
        commentCount: increment(1)
      });
      
      // 3. Send notification to post author
      if (currentUser.uid !== post.uid) {
        await addDoc(collection(db, 'users', post.uid, 'notifications'), {
           type: 'post_comment',
           fromUserId: currentUser.uid,
           toUserId: post.uid,
           postId: post.id,
           commentId: docRef.id,
           createdAt: serverTimestamp(),
           read: false
        });
      }

      // Optimistic UI updates
      setComments(prev => [{ id: docRef.id, ...newComment }, ...prev]);
      setPost(prev => ({ ...prev, commentCount: (prev.commentCount || 0) + 1 }));
      setCommentText('');
      
      // Ensure current user profile is cached for display
      if (!commentProfiles[currentUser.uid]) {
        const uSnap = await getDoc(doc(db, 'users', currentUser.uid));
        setCommentProfiles(prev => ({ ...prev, [currentUser.uid]: uSnap.exists() ? uSnap.data() : { name: 'Anonymous' } }));
      }
      
    } catch (err) {
      console.error("Failed to post comment", err);
      toast.error("Failed to post comment");
    } finally {
      setIsCommenting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex justify-center font-poppins selection:bg-[#0BD9E5]/30">
        <main className="w-full sm:w-[700px] min-h-screen border-x border-gray-800 flex flex-col items-center justify-center">
          <div className="w-8 h-8 rounded-full border-t-2 border-[#0BD9E5] animate-spin" />
        </main>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-black text-white flex justify-center font-poppins selection:bg-[#0BD9E5]/30">
        <main className="w-full sm:w-[700px] min-h-screen border-x border-gray-800 flex flex-col items-center justify-center p-8 text-center">
          <h2 className="text-[24px] font-extrabold mb-2 text-gray-500">Post not found</h2>
          <button 
            onClick={() => navigate(-1)}
            className="mt-4 px-6 py-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors font-bold"
          >
            Go Back
          </button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white flex justify-center font-poppins selection:bg-[#0BD9E5]/30">
      <main className="w-full sm:w-[700px] min-h-screen border-x border-gray-800 flex flex-col pb-20 sm:pb-0 shrink-0">
        {/* Header */}
        <div className="sticky top-0 bg-black/80 backdrop-blur-md z-40 border-b border-gray-800 flex items-center px-4 h-14">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 -ml-2 rounded-full hover:bg-white/10 transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="font-bold text-xl ml-4">Post</h1>
        </div>

        {/* Content */}
        {isRestricted ? (
          <div className="p-8 mt-4 mx-4 bg-[#111] border border-gray-800 border-dashed rounded-2xl flex flex-col items-center text-center">
             <h2 className="text-gray-400 font-bold text-lg mb-2">Limited Visibility</h2>
             <p className="text-gray-500 text-sm">
                This post is restricted to {post.visibility === 'campus' ? 'campus members' : 'followers'}.
             </p>
          </div>
        ) : (
          <div className="flex-1">
            <PostCard 
              post={post}
              userProfile={authorProfile}
              isLiked={likedPosts.has(post.id)}
              isSaved={savedPosts.has(post.id)}
              onLikeUpdate={handleLikeUpdate}
              onSaveUpdate={handleSaveUpdate}
            />
            
            {/* Comment Section */}
            <div className="border-t border-gray-800 flex flex-col mt-2">
               {/* Input Box */}
               <div className="p-4 flex gap-3 border-b border-gray-800">
                  <div className="w-10 h-10 rounded-full bg-gray-800 shrink-0 border border-gray-700"></div>
                  <div className="flex-1 flex items-center justify-between gap-4">
                     <input 
                        type="text" 
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        placeholder="Post your reply"
                        className="bg-transparent border-none outline-none text-white w-full text-[15px]"
                        onKeyDown={(e) => { if (e.key === 'Enter') handlePostComment(); }}
                     />
                     <button 
                        onClick={handlePostComment}
                        disabled={!commentText.trim() || isCommenting}
                        className={`bg-[#0BD9E5] text-black font-bold px-4 py-1.5 rounded-full text-[14px] transition-colors ${(!commentText.trim() || isCommenting) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#09bcc7]'}`}
                     >
                        Reply
                     </button>
                  </div>
               </div>
               
               {/* Comments List */}
               <div className="flex flex-col">
                  {comments.length === 0 ? (
                     <div className="p-8 text-center text-gray-500">
                        No replies yet. Be the first to share your thoughts!
                     </div>
                  ) : (
                     comments.map(c => {
                        const commenter = commentProfiles[c.userId] || { name: 'Anonymous' };
                        return (
                           <div key={c.id} className="p-4 border-b border-gray-800 flex gap-3 hover:bg-white/5 transition-colors">
                              <div className="w-10 h-10 rounded-full bg-gray-800 shrink-0 overflow-hidden border border-gray-700">
                                 {commenter.photoURL && <img src={commenter.photoURL} alt="" className="w-full h-full object-cover" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                 <div className="flex items-center gap-1.5 text-[15px]">
                                    <span className="font-bold text-white hover:underline cursor-pointer">{commenter.name || 'Anonymous'}</span>
                                    {commenter.isPremium && (
                                       <Sparkles size={14} className="text-[#0BD9E5] shrink-0" />
                                    )}
                                    <span className="text-gray-500">@{commenter.username || 'user'}</span>
                                    <span className="text-gray-500">·</span>
                                    <span className="text-gray-500">{formatRelativeTime(c.createdAt)}</span>
                                 </div>
                                 <p className="text-white text-[15px] mt-0.5 whitespace-pre-wrap break-words">{c.content}</p>
                              </div>
                           </div>
                        );
                     })
                  )}
               </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ViewPostPage;
